/**
 * ControlPoints - Renders draggable control points for geometry objects
 *
 * Shows corner/vertex points AND center points that can be dragged to modify:
 * - Rectangle: 4 corners + 1 center
 * - Polygon: N vertices + 1 center
 * - Box: 4 corners (2D) / 8 corners (3D) + 1 center
 * - Polyline: N vertices + 1 center
 * - Circle: 1 center + 1 radius point
 * - Line: 2 endpoints + 1 center
 */

import React, { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const Z_CONTROL = 15; // Z-level for control points (above other geometry)
const CONTROL_POINT_RADIUS = 100; // 10cm radius (larger for easier clicking)
const CENTER_POINT_RADIUS = 120; // 12cm for center points
const CONTROL_POINT_COLOR = '#22c55e'; // Green for corners
const CENTER_POINT_COLOR = '#3b82f6'; // Blue for center
const CONTROL_POINT_HOVER_COLOR = '#4ade80'; // Light green
const CONTROL_POINT_DRAG_COLOR = '#fbbf24'; // Yellow while dragging

/**
 * Single draggable control point using window-level drag events
 */
function ControlPoint({
  position,
  index,
  objectId,
  objectType, // 'curve' or 'solid'
  pointType = 'corner', // 'corner' or 'center'
  isHovered,
  onDragStart,
  onDrag,
  onDragEnd,
  onHover,
  viewMode = '2d',
}) {
  const { camera, raycaster, gl } = useThree();
  const meshRef = useRef();
  const [isDragging, setIsDragging] = useState(false);
  const dragPlane = useRef(new THREE.Plane(new THREE.Vector3(0, 0, 1), 0));
  const intersection = useRef(new THREE.Vector3());
  const mouse = useRef(new THREE.Vector2());

  // Calculate display position
  const displayPos = useMemo(() => {
    if (viewMode === '2d') {
      return [position.x, position.y, Z_CONTROL];
    }
    return [position.x, position.y, position.z || 0];
  }, [position, viewMode]);

  // Color based on state and type
  const color = useMemo(() => {
    if (isDragging) return CONTROL_POINT_DRAG_COLOR;
    if (isHovered) return CONTROL_POINT_HOVER_COLOR;
    return pointType === 'center' ? CENTER_POINT_COLOR : CONTROL_POINT_COLOR;
  }, [isDragging, isHovered, pointType]);

  const radius = pointType === 'center' ? CENTER_POINT_RADIUS : CONTROL_POINT_RADIUS;

  // Window-level drag handling
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      // Convert mouse position to normalized device coordinates
      const rect = gl.domElement.getBoundingClientRect();
      mouse.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      // Raycast to XY plane
      raycaster.setFromCamera(mouse.current, camera);
      if (raycaster.ray.intersectPlane(dragPlane.current, intersection.current)) {
        onDrag?.(objectId, index, {
          x: intersection.current.x,
          y: intersection.current.y,
          z: position.z || 0,
        }, objectType, pointType);
      }
    };

    const handleMouseUp = (e) => {
      setIsDragging(false);
      document.body.style.cursor = 'default';

      // Final position
      const rect = gl.domElement.getBoundingClientRect();
      mouse.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse.current, camera);
      if (raycaster.ray.intersectPlane(dragPlane.current, intersection.current)) {
        onDragEnd?.(objectId, index, {
          x: intersection.current.x,
          y: intersection.current.y,
          z: position.z || 0,
        }, objectType, pointType);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, objectId, index, objectType, pointType, position.z, onDrag, onDragEnd, camera, raycaster, gl]);

  const handlePointerDown = useCallback((e) => {
    e.stopPropagation();
    setIsDragging(true);
    document.body.style.cursor = 'grabbing';
    onDragStart?.(objectId, index, objectType, pointType);
  }, [objectId, index, objectType, pointType, onDragStart]);

  const handlePointerOver = useCallback((e) => {
    e.stopPropagation();
    onHover?.(objectId, index);
    if (!isDragging) {
      document.body.style.cursor = 'grab';
    }
  }, [objectId, index, onHover, isDragging]);

  const handlePointerOut = useCallback((e) => {
    if (!isDragging) {
      onHover?.(null, null);
      document.body.style.cursor = 'default';
    }
  }, [isDragging, onHover]);

  return (
    <mesh
      ref={meshRef}
      position={displayPos}
      onPointerDown={handlePointerDown}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
    >
      {viewMode === '2d' ? (
        <circleGeometry args={[radius, 16]} />
      ) : (
        <sphereGeometry args={[radius, 16, 8]} />
      )}
      <meshBasicMaterial color={color} />
    </mesh>
  );
}

/**
 * Calculate center point of an array of points
 */
function calculateCenter(points) {
  if (!points || points.length === 0) return null;
  let cx = 0, cy = 0, cz = 0;
  points.forEach(p => {
    cx += p.x;
    cy += p.y;
    cz += (p.z || 0);
  });
  return {
    x: cx / points.length,
    y: cy / points.length,
    z: cz / points.length,
  };
}

/**
 * Control points for a curve (Rectangle, Polygon, Polyline, Circle, Line)
 */
export function CurveControlPoints({
  curve,
  hoveredPointIndex,
  onDragStart,
  onDrag,
  onDragEnd,
  onHover,
  viewMode = '2d',
}) {
  // Get control points based on curve type
  const { cornerPoints, centerPoint } = useMemo(() => {
    if (!curve) return { cornerPoints: [], centerPoint: null };

    let corners = [];
    let center = null;

    // Rectangle: 4 corners + center
    if (curve.type === 'Rectangle') {
      const c1 = curve._corner1 || curve.corner1;
      const c2 = curve._corner2 || curve.corner2;
      if (c1 && c2) {
        corners = [
          { x: c1.x, y: c1.y, z: c1.z || 0 },          // 0: Bottom-left
          { x: c2.x, y: c1.y, z: c1.z || 0 },          // 1: Bottom-right
          { x: c2.x, y: c2.y, z: c2.z || 0 },          // 2: Top-right
          { x: c1.x, y: c2.y, z: c2.z || 0 },          // 3: Top-left
        ];
        center = {
          x: (c1.x + c2.x) / 2,
          y: (c1.y + c2.y) / 2,
          z: ((c1.z || 0) + (c2.z || 0)) / 2,
        };
      }
    }

    // Polygon: N vertices + center
    else if (curve.type === 'Polygon' && curve._points) {
      corners = curve._points.map(p => ({ x: p.x, y: p.y, z: p.z || 0 }));
      center = calculateCenter(corners);
    }

    // Polyline: N vertices + center
    else if (curve.type === 'Polyline' && curve._points) {
      corners = curve._points.map(p => ({ x: p.x, y: p.y, z: p.z || 0 }));
      center = calculateCenter(corners);
    }

    // Circle: center + radius point (on right side)
    else if (curve.type === 'Circle' && curve._center) {
      const c = curve._center;
      const r = curve._radius || 500;
      center = { x: c.x, y: c.y, z: c.z || 0 };
      // Add a radius control point
      corners = [
        { x: c.x + r, y: c.y, z: c.z || 0, isRadius: true }, // Radius point
      ];
    }

    // Arc: start, through, end + center
    else if (curve.type === 'Arc') {
      const c = curve._center;
      const r = curve._radius || 500;
      const startAngle = curve._startAngle || 0;
      const endAngle = curve._endAngle || Math.PI;
      if (c) {
        corners = [
          { x: c.x + r * Math.cos(startAngle), y: c.y + r * Math.sin(startAngle), z: c.z || 0 }, // Start
          { x: c.x + r * Math.cos(endAngle), y: c.y + r * Math.sin(endAngle), z: c.z || 0 }, // End
        ];
        center = { x: c.x, y: c.y, z: c.z || 0 };
      }
    }

    // Line: 2 endpoints + center
    else if (curve.type === 'Line') {
      const start = curve._start || curve.start;
      const end = curve._end || curve.end;
      if (start && end) {
        corners = [
          { x: start.x, y: start.y, z: start.z || 0 },
          { x: end.x, y: end.y, z: end.z || 0 },
        ];
        center = {
          x: (start.x + end.x) / 2,
          y: (start.y + end.y) / 2,
          z: ((start.z || 0) + (end.z || 0)) / 2,
        };
      }
    }

    return { cornerPoints: corners, centerPoint: center };
  }, [curve]);

  if (!curve) return null;

  // Center point index is after all corner points
  const centerIndex = cornerPoints.length;

  return (
    <group name={`controlPoints_${curve.id}`}>
      {/* Corner/vertex points */}
      {cornerPoints.map((pos, index) => (
        <ControlPoint
          key={`${curve.id}_corner_${index}`}
          position={pos}
          index={index}
          objectId={curve.id}
          objectType="curve"
          pointType="corner"
          isHovered={hoveredPointIndex === index}
          onDragStart={onDragStart}
          onDrag={onDrag}
          onDragEnd={onDragEnd}
          onHover={onHover}
          viewMode={viewMode}
        />
      ))}

      {/* Center point */}
      {centerPoint && (
        <ControlPoint
          key={`${curve.id}_center`}
          position={centerPoint}
          index={centerIndex}
          objectId={curve.id}
          objectType="curve"
          pointType="center"
          isHovered={hoveredPointIndex === centerIndex}
          onDragStart={onDragStart}
          onDrag={onDrag}
          onDragEnd={onDragEnd}
          onHover={onHover}
          viewMode={viewMode}
        />
      )}
    </group>
  );
}

/**
 * Control points for a solid (Box, Cylinder, Sphere)
 */
export function SolidControlPoints({
  solid,
  hoveredPointIndex,
  onDragStart,
  onDrag,
  onDragEnd,
  onHover,
  viewMode = '2d',
}) {
  // Get control points based on solid type
  const { cornerPoints, centerPoint } = useMemo(() => {
    if (!solid) return { cornerPoints: [], centerPoint: null };

    let corners = [];
    let center = null;

    // Box: 4/8 corners + center
    if (solid.type === 'Box' || solid.type === 'box') {
      const { origin, dimensions } = solid;
      const { width, depth, height } = dimensions;
      const hw = width / 2;
      const hd = depth / 2;
      const hh = height / 2;

      // 8 corners
      const allCorners = [
        // Bottom face (Z = origin.z - hh)
        { x: origin.x - hw, y: origin.y - hd, z: origin.z - hh },
        { x: origin.x + hw, y: origin.y - hd, z: origin.z - hh },
        { x: origin.x + hw, y: origin.y + hd, z: origin.z - hh },
        { x: origin.x - hw, y: origin.y + hd, z: origin.z - hh },
        // Top face (Z = origin.z + hh)
        { x: origin.x - hw, y: origin.y - hd, z: origin.z + hh },
        { x: origin.x + hw, y: origin.y - hd, z: origin.z + hh },
        { x: origin.x + hw, y: origin.y + hd, z: origin.z + hh },
        { x: origin.x - hw, y: origin.y + hd, z: origin.z + hh },
      ];

      // For 2D view, only show bottom 4 corners
      corners = viewMode === '2d' ? allCorners.slice(0, 4) : allCorners;
      center = { x: origin.x, y: origin.y, z: origin.z };
    }

    // Cylinder: center point only (origin is already center)
    else if (solid.type === 'Cylinder' || solid.type === 'cylinder') {
      center = { x: solid.origin.x, y: solid.origin.y, z: solid.origin.z };
      // Add radius control point
      const r = solid.dimensions?.radius || 500;
      corners = [
        { x: solid.origin.x + r, y: solid.origin.y, z: solid.origin.z, isRadius: true },
      ];
    }

    // Sphere: center point + radius point
    else if (solid.type === 'Sphere' || solid.type === 'sphere') {
      center = { x: solid.origin.x, y: solid.origin.y, z: solid.origin.z };
      const r = solid.dimensions?.radius || 500;
      corners = [
        { x: solid.origin.x + r, y: solid.origin.y, z: solid.origin.z, isRadius: true },
      ];
    }

    return { cornerPoints: corners, centerPoint: center };
  }, [solid, viewMode]);

  if (!solid) return null;

  // Center point index is after all corner points
  const centerIndex = cornerPoints.length;

  return (
    <group name={`controlPoints_${solid.id}`}>
      {/* Corner/vertex points */}
      {cornerPoints.map((pos, index) => (
        <ControlPoint
          key={`${solid.id}_corner_${index}`}
          position={pos}
          index={index}
          objectId={solid.id}
          objectType="solid"
          pointType="corner"
          isHovered={hoveredPointIndex === index}
          onDragStart={onDragStart}
          onDrag={onDrag}
          onDragEnd={onDragEnd}
          onHover={onHover}
          viewMode={viewMode}
        />
      ))}

      {/* Center point */}
      {centerPoint && (
        <ControlPoint
          key={`${solid.id}_center`}
          position={centerPoint}
          index={centerIndex}
          objectId={solid.id}
          objectType="solid"
          pointType="center"
          isHovered={hoveredPointIndex === centerIndex}
          onDragStart={onDragStart}
          onDrag={onDrag}
          onDragEnd={onDragEnd}
          onHover={onHover}
          viewMode={viewMode}
        />
      )}
    </group>
  );
}

/**
 * Main ControlPoints component - renders control points for all selected geometry
 */
export function ControlPoints({
  selectedCurves = [],
  selectedSolids = [],
  curves = [],
  solids = [],
  hoveredControlPoint = null, // { objectId, pointIndex }
  onControlPointDragStart,
  onControlPointDrag,
  onControlPointDragEnd,
  onControlPointHover,
  viewMode = '2d',
}) {
  // Get selected curve objects
  const selectedCurveObjects = useMemo(() => {
    const selectedSet = new Set(selectedCurves);
    return curves.filter(c => selectedSet.has(c.id));
  }, [curves, selectedCurves]);

  // Get selected solid objects
  const selectedSolidObjects = useMemo(() => {
    const selectedSet = new Set(selectedSolids);
    return solids.filter(s => selectedSet.has(s.id));
  }, [solids, selectedSolids]);

  // No selected objects, don't render
  if (selectedCurveObjects.length === 0 && selectedSolidObjects.length === 0) {
    return null;
  }

  return (
    <group name="controlPoints">
      {/* Curve control points */}
      {selectedCurveObjects.map(curve => (
        <CurveControlPoints
          key={curve.id}
          curve={curve}
          hoveredPointIndex={
            hoveredControlPoint?.objectId === curve.id
              ? hoveredControlPoint.pointIndex
              : null
          }
          onDragStart={onControlPointDragStart}
          onDrag={onControlPointDrag}
          onDragEnd={onControlPointDragEnd}
          onHover={(objectId, index) => onControlPointHover?.({ objectId, pointIndex: index })}
          viewMode={viewMode}
        />
      ))}

      {/* Solid control points */}
      {selectedSolidObjects.map(solid => (
        <SolidControlPoints
          key={solid.id}
          solid={solid}
          hoveredPointIndex={
            hoveredControlPoint?.objectId === solid.id
              ? hoveredControlPoint.pointIndex
              : null
          }
          onDragStart={onControlPointDragStart}
          onDrag={onControlPointDrag}
          onDragEnd={onControlPointDragEnd}
          onHover={(objectId, index) => onControlPointHover?.({ objectId, pointIndex: index })}
          viewMode={viewMode}
        />
      ))}
    </group>
  );
}

export default ControlPoints;
